// src/features/tickets/hooks/useDomainTickets.ts
//
// Rein lesender Hook: laedt Tickets ANDERER Kunden auf derselben E-Mail-Domain
// wie der aktuelle Kunde (F6, Teil 2). Die eigentliche Domain-Logik inklusive
// Ausschluss oeffentlicher Provider (gmail.com etc.) liegt serverseitig in der
// RPC get_domain_tickets - das Frontend dupliziert die Provider-Liste nicht.

import { useState, useEffect } from 'react';
import { supabase } from '../../../shared/services/supabase';
import { TicketStatus } from '../types';

export interface DomainTicketSummary {
  id: string;
  ticket_number: string;
  subject: string;
  status: TicketStatus;
  last_activity: string;
  customer_id: string;
  company: string | null;
}

export interface UseDomainTicketsResult {
  loading: boolean;
  tickets: DomainTicketSummary[];
}

const MAX_TICKETS = 8;

interface DomainTicketRow {
  id: string;
  ticket_number: string;
  subject: string;
  status: TicketStatus;
  last_activity: string;
  customer_id: string;
  company: string | null;
}

export const useDomainTickets = (customerId: string | null): UseDomainTicketsResult => {
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<DomainTicketSummary[]>([]);

  useEffect(() => {
    if (!customerId) {
      setTickets([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc('get_domain_tickets', {
          p_customer_id: customerId,
          p_limit: MAX_TICKETS
        });

        if (cancelled) return;

        if (error) {
          console.error('[useDomainTickets] RPC error:', error);
          setTickets([]);
          return;
        }

        const rows = (data ?? []) as DomainTicketRow[];
        setTickets(
          rows.map(r => ({
            id: r.id,
            ticket_number: r.ticket_number,
            subject: r.subject,
            status: r.status,
            last_activity: r.last_activity,
            customer_id: r.customer_id,
            company: r.company
          }))
        );
      } catch (err) {
        console.error('[useDomainTickets] Unexpected error:', err);
        if (!cancelled) setTickets([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  return { loading, tickets };
};
